import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

type VirtualScrollResult = {
  scrollRef: RefObject<HTMLDivElement | null>;
  visibleRange: { start: number; end: number };
  totalHeight: number;
  topPad: number;
  bottomPad: number;
  clampedEnd: number;
};

export function useVirtualScroll(
  itemCount: number,
  rowHeight: number,
  overscan: number = 10,
): VirtualScrollResult {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 30 });

  const updateVisibleRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const viewportHeight = el.clientHeight;
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const end = Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan;
    setVisibleRange((prev) => {
      if (prev.start === start && prev.end === end) return prev;
      return { start, end };
    });
  }, [rowHeight, overscan]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateVisibleRange, { passive: true });
    updateVisibleRange();
    return () => el.removeEventListener('scroll', updateVisibleRange);
  }, [updateVisibleRange]);

  useEffect(() => {
    updateVisibleRange();
  }, [itemCount, updateVisibleRange]);

  const totalHeight = itemCount * rowHeight;
  const clampedEnd = Math.min(visibleRange.end, itemCount);
  const topPad = visibleRange.start * rowHeight;
  const bottomPad = Math.max(0, (itemCount - clampedEnd) * rowHeight);

  return { scrollRef, visibleRange, totalHeight, topPad, bottomPad, clampedEnd };
}
